/**
 * Structural ("data-blind") question picker — TypeScript port of
 * `make_data_blind_picker` in
 * `MoDN/modn/scripts/experiment/min_questions/strategy.py`.
 *
 * For each candidate feature, substitute every "in-accept" representative plus a
 * single "not-in-accept" sentinel (so each atom contributes one True branch and
 * one False branch), compute the residual size of the criteria expression after
 * each substitution, and pick the candidate minimising the *mean* residual.
 * This rewards atoms whose False branch short-circuits an And (e.g. the entry
 * stem of a sub-criterion) — using only the boolean structure, no training-set
 * statistics, so it is DTA-clean by construction. Ties break by questionnaire
 * position (earlier first).
 */
import {Answer, Atom, atomsIn, Expr, Observed, residualSize} from './boolean';

export type PickFn = (cands: string[], observed: Observed) => string;

const SENTINEL = '<not in accept>';

const atomsByFeature = (expr: Expr): Map<string, Atom[]> => {
  const out = new Map<string, Atom[]>();
  for (const a of atomsIn(expr)) {
    const list = out.get(a.feature);
    if (list) {
      list.push(a);
    } else {
      out.set(a.feature, [a]);
    }
  }
  return out;
};

export const makeDataBlindPicker = (
  expr: Expr,
  featurePosition: Map<string, number>,
): PickFn => {
  const byFeature = atomsByFeature(expr);

  // One representative answer per atom (any accept value gives the atom's True
  // branch — residual is identical for any member of `accept`), plus the
  // not-in-accept sentinel for the False branch.
  const candidateValues = (feat: string): Answer[] => {
    const atoms = byFeature.get(feat) ?? [];
    const seen: Answer[] = [];
    for (const a of atoms) {
      for (const v of a.accept) {
        if (!seen.includes(v)) {
          seen.push(v);
          break;
        }
      }
    }
    const unionAccept = new Set<string | number>();
    for (const a of atoms) {
      for (const v of a.accept) {
        unionAccept.add(v);
      }
    }
    if (!unionAccept.has(SENTINEL) && !seen.includes(SENTINEL)) {
      seen.push(SENTINEL);
    }
    return seen;
  };

  const pos = (f: string): number => featurePosition.get(f) ?? 1e9;

  return (cands: string[], observed: Observed): string => {
    let bestFeat: string | null = null;
    let bestAvg = Infinity;
    for (const f of cands) {
      const vals = candidateValues(f);
      let total = 0;
      for (const v of vals) {
        const trial: Observed = new Map(observed);
        trial.set(f, v);
        total += residualSize(expr, trial);
      }
      const avg = total / vals.length;
      if (
        bestFeat === null ||
        avg < bestAvg ||
        (avg === bestAvg && pos(f) < pos(bestFeat))
      ) {
        bestFeat = f;
        bestAvg = avg;
      }
    }
    if (bestFeat === null) {
      throw new Error('pick() called with no candidates');
    }
    return bestFeat;
  };
};
